import * as React from "react";
import { makeStyles, Typography } from "@material-ui/core";
import { weight, fontFamilyHelvetica } from "@clockwise/web-commons/src/styles/type.styles";
import { colors, greys } from "@clockwise/web-commons/src/styles/color.styles";

// other components
import { LoginBtn } from "@components/login-btn";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";

// mutations
import { loginUser } from "@mutations";

// styles
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import { fontFamilySans, size } from "@clockwise/web-commons/src/styles/type.styles";
import spacing from "@clockwise/web-commons/src/styles/spacing.styles";

// material-ui
import { LinearProgress } from "@material-ui/core";

// util
import { TrackingEvents, track } from "@util/analytics.util";

// constants
import { paths } from "@clockwise/client-commons/src/constants/site";

const useStyles = makeStyles({
  container: {
    display: "flex",
    fontFamily: fontFamilyHelvetica,
    fontSize: "1rem",
    lineHeight: 1.2,
    background: "#EDF5FC",
    borderRadius: 8,
    marginTop: 100,
  },
  body: {
    padding: "16px 22px",
    width: "100%",
  },
  title: {
    color: greys.standard,
    fontWeight: weight.bold,
    margin: "0px 0px 16px",
  },
  form: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${greys.borderGrey}`,
    borderRadius: 4,
    overflow: "hidden",
  },
  textBox: {
    backgroundColor: greys.white,
    fontSize: "1.0rem",
    borderRadius: "4px 0 0 4px",
    flex: 1,

    "& input": {
      color: greys.darkestGrey,
    },

    "& placeholder": {
      color: greys.blueGrey,
    },

    "& .MuiInput-formControl": {
      padding: "6px 6px 3px 14px",
    },

    "& .Mui-focused": {
      borderBottom: `2px solid ${colors.green.darker}`,
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      marginBottom: -2, // Top offset the height added by the border to prevent content jumping down.
    },
  },
  button: {
    borderRadius: 0,
    padding: "9px 14px 8px 14px",
  },
  footer: {
    fontSize: 12,
    color: greys.slate,
    marginTop: 10,
    textAlign: "center",
  },
  anchor: {
    color: greys.standard,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  caption: {
    color: greys.slate,
    width: 230,
    textAlign: "center",
  },
});

const signupBtn: { [key: string]: CSSProperties } = {
  base: {
    height: 45,
    lineHeight: `45px`,
    width: "100%",
  },
  label: {
    fontWeight: 400,
    fontFamily: fontFamilySans,
    fontSize: size.std,
    paddingRight: spacing.lg,
    color: greys.white,
    textTransform: "none",
    width: "100%",
  },
};

const loadingBar: CSSProperties = {
  backgroundColor: "transparent",
  position: "absolute",
  bottom: 0,
  width: "100%",
};

export const SignupBanner = () => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const [hideCookieErrorDialog, setHideCookieErrorDialog] = React.useState(true);

  React.useEffect(() => {
    track(TrackingEvents.LINKS.CONFIRMATION_PAGE.VARIANT_GOOGLE_SIGN_IN_VIEW);
  }, []);

  const onLoginStart = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    setLoading(true);
  };
  const onLoginSuccess = () => {
    track(TrackingEvents.AUTH.LOGIN);
  };

  const onLoginFailure = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    setLoading(false);
  };

  const onGoogleFailure = (e: any) => {
    if (e.error === "idpiframe_initialization_failed") {
      setHideCookieErrorDialog(false);
    }
  };
  const renderCookieDialog = () => {
    return <CookieDialog isChromeExtension={false} open={!hideCookieErrorDialog} />;
  };

  const onClickSignup = () => {
    track(TrackingEvents.LINKS.CONFIRMATION_PAGE.VARIANT_GOOGLE_SIGN_IN_CLICKED);
  };

  return (
    <div>
      <div className={classes.container}>
        {renderCookieDialog()}
        <div className={classes.body}>
          <p className={classes.title}>🔗 Create your own scheduling link with Clockwise</p>
          <div className={classes.form}>
            <LoginBtn
              googleStandard
              label="Sign up with Google for free"
              loginUserMutation={loginUser}
              style={signupBtn.base}
              labelStyle={signupBtn.label}
              redirect={"onboarding"}
              onStart={onLoginStart}
              onSuccess={onLoginSuccess}
              onFailure={onLoginFailure}
              onGoogleFailure={onGoogleFailure}
              onClick={onClickSignup}
            />
          </div>
          <div className={classes.footer}>
            <Typography className={classes.caption} variant="caption">
              By signing in, you agree to Clockwise's{" "}
              <a className={classes.anchor} href={paths.terms} target="_blank">
                terms of service
              </a>{" "}
              and{" "}
              <a className={classes.anchor} href={paths.privacy} target="_blank">
                privacy policy
              </a>
              .
            </Typography>
          </div>
        </div>
      </div>
      {loading && <LinearProgress style={loadingBar} variant="indeterminate" />}
    </div>
  );
};
